import React, { useState } from 'react'
import { api } from '../../lib/api'
import { clickableRowProps } from '../../lib/a11y'
import { useOverlayA11y } from '../../hooks/useOverlayA11y'
import { MatchBandBadge, RiskBadge } from '../../components/ui'
import { CLINICAL_CONCERNS } from '../../lib/vocab'
import type { MatchResult } from '../../lib/types'

export function AddPatientModal({ onClose, onCaseCreated }: { onClose: () => void; onCaseCreated: () => void }) {
  const [step, setStep] = useState<'patient' | 'intake' | 'matches'>('patient')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const modalRef = useOverlayA11y(onClose)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const [concerns, setConcerns] = useState<string[]>([])
  const [modality, setModality] = useState('')
  const [language, setLanguage] = useState('')
  const [state, setState] = useState('')
  const [format, setFormat] = useState('either')

  const [patientId, setPatientId] = useState<string | null>(null)
  const [intakeId, setIntakeId] = useState<string | null>(null)
  const [matches, setMatches] = useState<MatchResult[]>([])

  function toggleConcern(c: string) {
    setConcerns((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]))
  }

  async function submitPatient(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const patient = await api('/patients', { method: 'POST', body: JSON.stringify({ name, email: email || null, phone: phone || null }) })
      setPatientId(patient.id)
      setStep('intake')
    } catch {
      setError('Could not create patient.')
    } finally {
      setBusy(false)
    }
  }

  async function submitIntake(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const intake = await api('/intake', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: patientId,
          presenting_concerns: concerns,
          modality_preference: modality || null,
          language_preference: language || null,
          logistics: { state: state || null, format },
          preferences: {},
          budget_or_insurance_context: {},
        }),
      })
      setIntakeId(intake.id)
      const ranked: MatchResult[] = await api('/v1/matches', { method: 'POST', body: JSON.stringify({ intake_id: intake.id }) })
      setMatches(ranked)
      setStep('matches')
    } catch {
      setError('Could not generate matches.')
    } finally {
      setBusy(false)
    }
  }

  async function selectMatch(matchId: string) {
    setBusy(true)
    setError(null)
    try {
      await api('/v1/cases', { method: 'POST', body: JSON.stringify({ match_id: matchId }) })
      onCaseCreated()
      onClose()
    } catch {
      setError('Could not open case from this match.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Add patient and intake" tabIndex={-1}>
        <h2>Add patient + intake</h2>
        <p className="muted" style={{ fontSize: 13.5, marginTop: -8 }}>
          Creates a real patient, intake, and runs the R&amp;C engine for matches — no demo data.
        </p>

        {step === 'patient' && (
          <form onSubmit={submitPatient}>
            <div className="field"><label>Patient name</label><input className="inp" value={name} onChange={(e) => setName(e.target.value)} required /></div>
            <div className="field"><label>Email (optional)</label><input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <div className="field"><label>Phone (optional)</label><input className="inp" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
            {error && <div className="form-error">{error}</div>}
            <button className="btn full" disabled={busy}>{busy ? 'Saving…' : 'Continue to intake'}</button>
          </form>
        )}

        {step === 'intake' && (
          <form onSubmit={submitIntake}>
            <div className="field">
              <label id="concerns-label">Presenting concerns</label>
              <div className="chip-list" role="group" aria-labelledby="concerns-label">
                {CLINICAL_CONCERNS.map((c) => (
                  <button type="button" key={c.value} className="chip" aria-pressed={concerns.includes(c.value)} style={{ background: concerns.includes(c.value) ? '#6B4EFF' : '#F1EEFB', color: concerns.includes(c.value) ? 'white' : '#6B4EFF' }} onClick={() => toggleConcern(c.value)}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-grid">
              <div className="field"><label>Modality preference</label><input className="inp" value={modality} onChange={(e) => setModality(e.target.value)} placeholder="CBT, psychodynamic…" /></div>
              <div className="field"><label>Language preference</label><input className="inp" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="English" /></div>
              <div className="field"><label>State / province</label><input className="inp" value={state} onChange={(e) => setState(e.target.value)} placeholder="AB" /></div>
              <div className="field">
                <label>Session format</label>
                <select className="inp" value={format} onChange={(e) => setFormat(e.target.value)}>
                  <option value="either">Either</option>
                  <option value="virtual">Virtual</option>
                  <option value="in_person">In person</option>
                </select>
              </div>
            </div>
            {error && <div className="form-error">{error}</div>}
            <button className="btn full" disabled={busy}>{busy ? 'Matching…' : 'Generate matches'}</button>
          </form>
        )}

        {step === 'matches' && (
          <div>
            {matches.length === 0 && <div className="empty">No providers passed the routing gates for this intake yet.</div>}
            <div className="queue-list">
              {matches.map((m) => (
                <div key={m.id} className="queue-item" {...clickableRowProps(() => selectMatch(m.id))}>
                  <div className="qi-main">
                    <span>Provider {m.provider_id.slice(0, 8)}</span>
                    <span className="qi-reason">{m.match_reasons.join(' · ')}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <MatchBandBadge band={m.match_band} />
                    <RiskBadge band={m.risk_band} />
                  </div>
                </div>
              ))}
            </div>
            {error && <div className="form-error">{error}</div>}
          </div>
        )}
      </div>
    </div>
  )
}
